import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as Yup from 'yup';

import { BadgeColor } from 'components/Display/Badge/Badge';
import { Section } from 'components/Display/Section/Section';
import { EditableStringLists, EditableStringListSpec } from 'components/Form/EditableStringLists/EditableStringLists';

import { SENDER_PATTERN_REGEX } from '../../../helpers';

type SenderFilterSectionProps = {
  whitelist: Array<string>;
  blacklist: Array<string>;
  onApply: (whitelist: Array<string>, blacklist: Array<string>) => void;
  disabled: boolean;
};

export const SenderFilterSection = ({ whitelist, blacklist, onApply, disabled }: SenderFilterSectionProps) => {
  const { t } = useTranslation();
  const schema = useMemo(() => createSchema(t), [t]);

  const specs: Array<EditableStringListSpec> = [
    {
      name: 'whitelist',
      values: whitelist,
      badgeColor: BadgeColor.gray,
      sort: false,
      displayLabel: t('settings:documentUnderstandingAgent.global.senderFilter.allowlist'),
      displayEmptyLabel: t('settings:documentUnderstandingAgent.global.senderFilter.empty'),
      editLabel: t('settings:documentUnderstandingAgent.global.senderFilter.allowlist'),
      addLabel: t('settings:documentUnderstandingAgent.global.senderFilter.addAllowlist'),
      removeLabel: t('settings:documentUnderstandingAgent.global.senderFilter.remove'),
      addAnalyticsId: 'document-understanding-agent:add-whitelist',
      removeAnalyticsId: 'document-understanding-agent:remove-whitelist',
    },
    {
      name: 'blacklist',
      values: blacklist,
      badgeColor: BadgeColor.red,
      sort: false,
      displayLabel: t('settings:documentUnderstandingAgent.global.senderFilter.blocklist'),
      displayEmptyLabel: t('settings:documentUnderstandingAgent.global.senderFilter.empty'),
      editLabel: t('settings:documentUnderstandingAgent.global.senderFilter.blocklist'),
      addLabel: t('settings:documentUnderstandingAgent.global.senderFilter.addBlocklist'),
      removeLabel: t('settings:documentUnderstandingAgent.global.senderFilter.remove'),
      addAnalyticsId: 'document-understanding-agent:add-blacklist',
      removeAnalyticsId: 'document-understanding-agent:remove-blacklist',
    },
  ];

  return (
    <Section.Horizontal
      title={t('settings:documentUnderstandingAgent.global.senderFilter.title')}
      description={t('settings:documentUnderstandingAgent.global.senderFilter.description')}
    >
      <EditableStringLists
        specs={specs}
        schema={schema}
        title={t('settings:documentUnderstandingAgent.global.senderFilter.modal.title')}
        subtitle={t('settings:documentUnderstandingAgent.global.senderFilter.modal.subtitle')}
        editAnalyticsId="document-understanding-agent:edit-sender-filter"
        cancelAnalyticsId="document-understanding-agent:cancel-edit"
        applyAnalyticsId="document-understanding-agent:apply-edit"
        onApply={(values) => onApply(values.whitelist, values.blacklist)}
        disabled={disabled}
      />
    </Section.Horizontal>
  );
};

const createSchema = (t: TFunction) => {
  const patternList = Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string()
          .required(t('settings:documentUnderstandingAgent.global.senderFilter.invalidPattern'))
          .matches(SENDER_PATTERN_REGEX, t('settings:documentUnderstandingAgent.global.senderFilter.invalidPattern')),
      }),
    )
    .required();

  return Yup.object().shape({ whitelist: patternList, blacklist: patternList });
};
